import { Fragment, useEffect, useMemo, useState } from 'react';
import time from '~/assets/icons/clock_3.svg';
import keyboard from '~/assets/icons/keyboard.svg';
import userRound from '~/assets/icons/user_round.svg';
import { rage, useObservable } from '~/rage';
import { PhoneCallStatus } from '~/shared';
import { AppContactsAddContact } from '../../components/AppContactsAddContact';
import { AppContactsAllContacts } from '../../components/AppContactsAllContacts';
import { AppContactsInfoContact } from '../../components/AppContactsInfoContact';
import { AppPhoneCall } from '../../components/AppPhoneCall/AppPhoneCall';
import { AppPhoneCallList } from '../../components/AppPhoneCallList';
import { AppPhoneIncomingCall } from '../../components/AppPhoneIncomingCall';
import { AppPhoneNumber } from '../../components/AppPhoneNumber';
import { ContactsStages } from '../../models/Contacts';
import { PhoneMenuItems } from '../../models/Phone';
import {
	setActiveContactsState,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import {
	StyledContainer,
	StyledIconWrapper,
	StyledMenu,
} from './PhonePhone.styled';

interface PhonePhoneLayoutProps {
	initialMenuItem: PhoneMenuItems;
}

const PhonePhoneLayout = ({ initialMenuItem }: PhonePhoneLayoutProps) => {
	const dispatch = useLayoutDispatch();
	const phoneState = useObservable(rage.phoneCalls.getCallState());
	const [activeMenuItem, setActiveMenuItem] =
		useState<PhoneMenuItems>(initialMenuItem);
	const activeContactsState = useLayoutSelector(
		(state) => state.contacts.activeContactsState,
	);

	useEffect(() => {
		setActiveMenuItem(initialMenuItem);
	}, [initialMenuItem]);

	useEffect(() => {
		return () => {
			dispatch(setActiveContactsState(ContactsStages.ALL_CONTACTS));
		};
	}, [dispatch]);

	const isIncomingCall = useMemo(() => {
		return phoneState?.status === PhoneCallStatus.INCOMING;
	}, [phoneState]);

	const isCall = useMemo(() => {
		return (
			phoneState?.status === PhoneCallStatus.CONNECTED ||
			phoneState?.status === PhoneCallStatus.DIALING
		);
	}, [phoneState]);

	const isContactsInnerScreen = useMemo(() => {
		if (activeMenuItem !== PhoneMenuItems.CONTACTS) {
			return false;
		}

		return (
			activeContactsState === ContactsStages.ADD_CONTACT ||
			activeContactsState === ContactsStages.INFO_CONTACT
		);
	}, [activeMenuItem, activeContactsState]);

	return (
		<StyledContainer>
			<StyledMenu $hidden={isIncomingCall || isCall || isContactsInnerScreen}>
				<StyledIconWrapper
					$active={activeMenuItem === PhoneMenuItems.CALL_HISTORY}
					onClick={() => setActiveMenuItem(PhoneMenuItems.CALL_HISTORY)}
				>
					<img src={time} />
				</StyledIconWrapper>
				<StyledIconWrapper
					$active={activeMenuItem === PhoneMenuItems.CONTACTS}
					onClick={() => setActiveMenuItem(PhoneMenuItems.CONTACTS)}
				>
					<img src={userRound} />
				</StyledIconWrapper>
				<StyledIconWrapper
					$active={activeMenuItem === PhoneMenuItems.NUMBER}
					onClick={() => setActiveMenuItem(PhoneMenuItems.NUMBER)}
				>
					<img src={keyboard} />
				</StyledIconWrapper>
			</StyledMenu>
			{isIncomingCall && <AppPhoneIncomingCall />}
			{(!phoneState || phoneState?.status === PhoneCallStatus.ENDED) && (
				<Fragment>
					{activeMenuItem === PhoneMenuItems.NUMBER && <AppPhoneNumber />}
					{activeMenuItem === PhoneMenuItems.CALL_HISTORY && (
						<AppPhoneCallList />
					)}
					{activeMenuItem === PhoneMenuItems.CONTACTS && (
						<Fragment>
							{activeContactsState === ContactsStages.ALL_CONTACTS && (
								<AppContactsAllContacts />
							)}
							{activeContactsState === ContactsStages.ADD_CONTACT && (
								<AppContactsAddContact />
							)}
							{activeContactsState === ContactsStages.INFO_CONTACT && (
								<AppContactsInfoContact />
							)}
						</Fragment>
					)}
				</Fragment>
			)}
			{isCall && <AppPhoneCall />}
		</StyledContainer>
	);
};

const PhonePhone = () => {
	return <PhonePhoneLayout initialMenuItem={PhoneMenuItems.CALL_HISTORY} />;
};

export { PhonePhone, PhonePhoneLayout };
